import { DateTime } from 'luxon';
import { StayCheckInMethod } from '../../../__generated__/graphql';

/**
 * Phase 5 helpers for the stay pages (spec 016 W18, W22, W23, W25): check-in details,
 * reviews, the host's Today sections and Resolution Center requests. Pure, so tested.
 */

const CHECK_IN_METHODS: Record<StayCheckInMethod, string> = {
  [StayCheckInMethod.SmartLock]: 'Smart lock',
  [StayCheckInMethod.Keypad]: 'Keypad',
  [StayCheckInMethod.Lockbox]: 'Lockbox',
  [StayCheckInMethod.HostGreets]: 'The host greets you',
  [StayCheckInMethod.BuildingStaff]: 'Building staff',
};

export function checkInMethodLabel(method?: StayCheckInMethod | null): string | null {
  return method ? (CHECK_IN_METHODS[method] ?? null) : null;
}

/** "15:00" → "3:00 PM". Anything else comes back as it is. */
export function clockLabel(time?: string | null): string {
  if (!time) return '';
  const parsed = DateTime.fromFormat(time, 'HH:mm');
  return parsed.isValid ? parsed.toFormat('h:mm a') : time;
}

/**
 * Whether the guest can open the check-in details now, or the sentence saying when.
 * The server decides (48 hours before check-in); this only words it.
 */
export function instructionsState(
  availableAt: string | Date | null | undefined,
  zone: string | null | undefined,
  now = Date.now(),
): { open: boolean; text: string } {
  if (!availableAt) {
    return { open: false, text: 'Check-in details are shared once the stay is confirmed.' };
  }
  const at = new Date(availableAt).getTime();
  if (at <= now) return { open: true, text: 'Your check-in details are ready.' };
  const when = DateTime.fromMillis(at, { zone: zone || 'UTC' }).toFormat('EEE, MMM d, h:mm a');
  return {
    open: false,
    text: `Door code, Wi-Fi and directions open ${when}, 48 hours before check-in. We email them too.`,
  };
}

// ─── reviews ────────────────────────────────────────────────────────────────

export const STAY_REVIEW_WINDOW_DAYS = 14;

export type StayGuestCategory =
  | 'cleanliness'
  | 'accuracy'
  | 'checkIn'
  | 'communication'
  | 'location'
  | 'value';

/** The six things a guest rates a place on (spec 016 §4.7), in Airbnb's order. */
export const GUEST_REVIEW_CATEGORIES: { key: StayGuestCategory; label: string; hint: string }[] = [
  { key: 'cleanliness', label: 'Cleanliness', hint: 'Was the place clean and tidy?' },
  { key: 'accuracy', label: 'Accuracy', hint: 'Did the listing match the place?' },
  { key: 'checkIn', label: 'Check-in', hint: 'Was getting in easy?' },
  { key: 'communication', label: 'Communication', hint: 'Did the host answer quickly?' },
  { key: 'location', label: 'Location', hint: 'Was the area as described?' },
  { key: 'value', label: 'Value', hint: 'Was it worth the price?' },
];

export type StayHostCategory = 'cleanliness' | 'communication' | 'houseRules';

export const HOST_REVIEW_CATEGORIES: { key: StayHostCategory; label: string; hint: string }[] = [
  { key: 'cleanliness', label: 'Cleanliness', hint: 'Did they leave the place tidy?' },
  { key: 'communication', label: 'Communication', hint: 'Were they easy to reach?' },
  { key: 'houseRules', label: 'House rules', hint: 'Did they follow your rules?' },
];

/** Every score is a whole number from 1 to 5. */
export function reviewComplete(
  scores: Record<string, number | undefined>,
  keys: string[],
): boolean {
  return keys.every(key => {
    const value = scores[key];
    return typeof value === 'number' && value >= 1 && value <= 5 && Number.isInteger(value);
  });
}

export type StayReviewView =
  | { kind: 'write'; text: string }
  | { kind: 'waiting'; text: string }
  | { kind: 'done'; text: string }
  | { kind: 'closed'; text: string }
  | { kind: 'not_yet'; text: string };

/**
 * What the review section says, from one side. `mine` and `theirs` are when each side
 * reviewed (the server hides the other side's words until both are in or 14 days pass).
 */
export function stayReviewView(input: {
  canRate: boolean;
  deadline?: string | Date | null;
  reason?: string | null;
  mine?: string | Date | null;
  theirs?: string | Date | null;
  otherName: string;
  zone?: string | null;
  now?: number;
}): StayReviewView {
  const now = input.now ?? Date.now();
  const deadline = input.deadline ? new Date(input.deadline).getTime() : null;
  const by = deadline
    ? DateTime.fromMillis(deadline, { zone: input.zone || 'UTC' }).toFormat('MMM d')
    : null;
  if (input.canRate) {
    return {
      kind: 'write',
      text: `Reviews show once you both write one${by ? `, or on ${by}` : ''}. Write yours${by ? ` by ${by}` : ''}.`,
    };
  }
  if (input.mine) {
    if (input.theirs) return { kind: 'done', text: 'You both left reviews.' };
    const revealed = deadline !== null && now >= deadline;
    return revealed
      ? { kind: 'done', text: `Your review is public. ${input.otherName} did not write one.` }
      : {
          kind: 'waiting',
          text: `Thanks for your review. It shows when ${input.otherName} writes theirs${by ? ` or on ${by}` : ''}.`,
        };
  }
  if (input.reason === 'Reviews open at checkout') {
    return { kind: 'not_yet', text: 'You can review this stay after checkout.' };
  }
  return { kind: 'closed', text: 'The review window for this stay has closed.' };
}

/** "4.8" for an average, "New" when there is none yet. */
export function averageLabel(value?: number | null): string {
  return value && value > 0 ? (Math.round(value * 10) / 10).toFixed(1) : 'New';
}

// ─── host Today ─────────────────────────────────────────────────────────────

export type TodaySectionKey =
  | 'pendingRequests'
  | 'checkingOut'
  | 'arrivingToday'
  | 'currentlyHosting'
  | 'upcoming'
  | 'pendingReviews';

/** The Today page's sections in the order the host acts on them. */
export const TODAY_SECTIONS: { key: TodaySectionKey; title: string; empty: string }[] = [
  { key: 'pendingRequests', title: 'Requests to answer', empty: 'No requests waiting.' },
  { key: 'checkingOut', title: 'Checking out today', empty: 'Nobody checks out today.' },
  { key: 'arrivingToday', title: 'Arriving today', empty: 'Nobody arrives today.' },
  { key: 'currentlyHosting', title: 'Currently hosting', empty: 'No guests are staying now.' },
  { key: 'upcoming', title: 'Upcoming', empty: 'No upcoming stays.' },
  { key: 'pendingReviews', title: 'Reviews to write', empty: 'No reviews to write.' },
];

/** "3 things need you" for the Today header. */
export function todaySummary(counts: Partial<Record<TodaySectionKey, number>>): string {
  const act =
    (counts.pendingRequests ?? 0) + (counts.checkingOut ?? 0) + (counts.arrivingToday ?? 0);
  if (act === 0) return 'Nothing needs you right now.';
  return `${act} ${act === 1 ? 'thing needs' : 'things need'} you today.`;
}

// ─── Resolution Center ──────────────────────────────────────────────────────

export type StayRequestCategory = 'damage' | 'missing' | 'cleaning' | 'other';

export const STAY_REQUEST_CATEGORIES: {
  key: StayRequestCategory;
  label: string;
  hint: string;
}[] = [
  { key: 'damage', label: 'Damage', hint: 'Something in the place was broken or damaged.' },
  { key: 'missing', label: 'Missing items', hint: 'Something was taken or not returned.' },
  { key: 'cleaning', label: 'Extra cleaning', hint: 'The place needed more than usual cleaning.' },
  { key: 'other', label: 'Something else', hint: 'Another problem with this stay.' },
];

/** The claim description: the category first, so admins and the other side see it. */
export function stayRequestDescription(category: StayRequestCategory, text: string): string {
  const label = STAY_REQUEST_CATEGORIES.find(c => c.key === category)?.label ?? 'Request';
  return `${label}: ${text.trim()}`;
}

/** Amount entered as text: a positive number with at most two decimals, else null. */
export function parseRequestAmount(value: string): number | null {
  const trimmed = value.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const amount = Number(trimmed);
  return amount > 0 && amount <= 100000 ? amount : null;
}

/** "Open until Mar 21, 11:00 AM" from the server's deadline. */
export function requestDeadlineLabel(
  deadline: string | null | undefined,
  zone?: string | null,
): string | null {
  if (!deadline) return null;
  const at = new Date(deadline).getTime();
  if (!Number.isFinite(at)) return null;
  return `Open until ${DateTime.fromMillis(at, { zone: zone || 'UTC' }).toFormat('MMM d, h:mm a')}`;
}
